import { useCallback, useEffect, useRef, useState } from "react";
import EditPodModal from "./PodModals/EditPodModal";
import { deleteFleet, getFleet, getPaginatedFleets } from "../lib/api";
import PodModal from "./PodModals/CreatePodModal";
import ConfirmModal from "./Common/ConfirmModal";
import SearchComponent from "./Common/SearchComponent";
import useFleetStore from "../utils/store";
import { fetchAllPods } from "../utils/fleetUtils";
import ToastMessage from "./Common/ToastMessage";
import GridListViewComponent from "./Common/GridListViewComponent";
import ZoneList from "./ZoneList";
import { useNavigate } from "react-router-dom";
import LoadingModal from "./Common/LoadingModal";
import Pagination from "./Common/Pagination";
import ZoneGrid from "./ZoneGrid";
import { SearchContext } from "../lib/analyticsEvents";
import GenerateReportModal from "./Common/GenerateReportModal";
import { useIsWriteRestrictedUser } from "../hooks/useIsWriteRestrictedUser";

const DEFAULT_PAGE_SIZE = 25;
const GRID_VIEW_STORAGE_KEY = "manageZonesViewMode";

export default function ManagePod() {
    const isAccessRestricted = useIsWriteRestrictedUser();
    const getStoredGridView = () => {
        if (window.innerWidth < 1025) return true;
        try {
            const stored = localStorage.getItem(GRID_VIEW_STORAGE_KEY);
            return stored ? stored === "grid" : true;
        } catch (error) {
            return true;
        }
    };
    const [activeDropdownId, setActiveDropdownId] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const fleetStore = useFleetStore();
    const { selectedFleet, setSelectedFleet } = fleetStore;
    const [selectedPodId, setSelectedPodId] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedPodData, setSelectedPodData] = useState(null);
    const dropdownRef = useRef(null);
    const [showCreatePodModal, setCreateShowPodModal] = useState(false);
    const [searchUnitText, setSearchUnitText] = useState(null);
    const [gridView, setGridView] = useState(() => getStoredGridView());
    const [allPodData, setAllPodData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const navigate = useNavigate();
    const [isGenerateReportModalOpen, setIsGenerateReportModalOpen] = useState(false);
    const [reportPrefillData, setReportPrefillData] = useState(null);
    const [toast, setToast] = useState({
        show: false,
        message: '',
        type: 'success',
    });

    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);

    const showToast = useCallback((message, type = 'success') => {
        setToast({ show: true, message, type });
    }, [setToast]);

    const toggleDropdown = (id) => {
        setActiveDropdownId(prev => (prev === id ? null : id));
    };

    // const searchFleet = (searchText) => {
    //     setAllPodData([])
    //     setPageNumber(1);

    // }

    const fetchPaginatedFleets = useCallback(async (append = true, searchText = searchUnitText || "") => {
        try {
            setLoading(true);

            const res = await getPaginatedFleets({
                pageNumber: currentPage + 1,
                pageSize: DEFAULT_PAGE_SIZE,
                search: searchText,
            });

            const newFleets = res?.data?.zones || [];
            const total = res?.data?.count || 0;

            if (searchText === "" && newFleets?.length === 0) {
                navigate('/landingpage');
            }

            setAllPodData(newFleets);
            setTotalCount(total);
        } catch (error) {
            console.error("Error fetching fleets:", error);
            showToast("Failed to load zones", "error");
        } finally {
            setLoading(false);
        }
    }, [showToast, navigate, searchUnitText, currentPage]);

    useEffect(() => {
        fetchPaginatedFleets(true, searchUnitText);
    }, [pageNumber, fetchPaginatedFleets, searchUnitText]);

    const handleEditClick = async (podId) => {
        setShowEditModal(false); // Don't show modal yet

        try {
            const data = await getFleet(podId);
            setSelectedPodData(data);
            setShowEditModal(true);
        } catch (err) {
            console.error("Error fetching pod data", err);
        }
    };

    const handleDeletePod = (podId, unitCount) => {
        setSelectedPodId(podId);
        if (unitCount >= 1) {
            showToast("Cannot delete the zone if it has units", 'failed');
        } else {
            setShowConfirmModal(true);
        }
    };

    const handleConfirmDelete = async () => {
        const payload = { unid: selectedPodId };
        try {
            const res = await deleteFleet(payload);
            if (res.status === 200) {
                showToast("Deleted Successfully", 'success');
                if (selectedFleet?.unid === selectedPodId) {
                    const filterdPods = allPodData?.filter(item => item.unid !== selectedPodId);

                    setSelectedFleet(filterdPods.length > 0 ? filterdPods[0] : null)
                }
                setShowConfirmModal(false);
                setAllPodData([])
                fetchPaginatedFleets(false, searchUnitText); // Reset pagination and fetch again
                fetchAllPods(); // Refresh the shared zone cache used by unit-assignment dropdowns

            } else {
                showToast("'Deleted Failed Due to network error'", 'failed');
                setShowConfirmModal(false);
            }
        } catch (err) {
            console.error("Error Deleting Pod", err);
            if (err.status === 409) {
                showToast("Cannot delete the zone if it has units", 'failed');
            } else {
                showToast("Delete Failed Due to network error", 'failed');
            }
            setShowConfirmModal(false);
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmModal(false);
        setSelectedPodId(null);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setActiveDropdownId(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const getTimeForZone = (timeZone) => {
        try {
            return new Intl.DateTimeFormat('en-US', {
                timeZone,
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            }).format(new Date());
        } catch {
            return '--:--';
        }
    };

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 1025) {
                setGridView(true);  // Force gridView to true on small screens
                return;
            }
            setGridView(getStoredGridView());
        };

        handleResize(); // check on mount

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (window.innerWidth < 1025) return;
        try {
            localStorage.setItem(GRID_VIEW_STORAGE_KEY, gridView ? "grid" : "list");
        } catch (error) {
            // Ignore localStorage write failures (e.g. privacy mode).
        }
    }, [gridView]);

    const handleNavigate = () => {
        setSelectedFleet(selectedFleet);
        navigate('/dashboard')
    }

    const handleSelectZone = (item) => {
        setSelectedFleet(item);
        navigate('/dashboard')
    }

    const handleNavigateToUnits = (item) => {
        setSelectedFleet(item);
        navigate('/units')
    }

    const handleGenerateReport = (zone) => {
        setActiveDropdownId(null);
        setReportPrefillData({
            fleetUnid: zone?.unid,
            reportType: "ZONE_WIDE",
        });
        setIsGenerateReportModalOpen(true);
    };

    const pageCount = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);

    const handlePageClick = ({ selected }) => {
        setCurrentPage(selected);
    };


    return (
        <>
            {/* {initialLoading &&( <Loader/>)} */}
            <div className="titleAndFilters" data-testid="managepod-title-and-filters">
                <div className="flex flex-col md:flex-row  gap-3  justify-between  items-start md:items-center">
                    <div className="flex gap-4 items-center justify-between w-full sm:w-auto sm:justify-normal">
                        <h2 className="mainTitle text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]" data-testid="managepod-title">Manage Zones {totalCount > 0 && (
                            <span data-testid="managepod-title-count">({totalCount})</span>)}</h2>
                        <span className="text-xl leading-5 lg:text-2xl font-normal text-[#AFC3BC] dark:text-[#AFC3BCCC] lg:leading-6 tracking-[0.036px] hidden sm:block">|</span>
                        <h2 className="mainTitle text-[#FF8C65] font-semibold text-md leading-5 lg:text-base lg:leading-6 tracking-[0.036px] cursor-pointer underline" onClick={handleNavigate} data-testid="managepod-goto-dashboard-button">Go to Dashboard</h2>
                    </div>
                    <div className="searchAndFilter flex items-center justify-between gap-x-4 gap-y-2 flex-wrap flex-row-reverse md:flex-row md:justify-items-end w-full md:w-auto">
                        <div className="flex items-center gap-4">
                            <SearchComponent searchUnitText={searchUnitText} setSearchUnitText= {(val) => {
                            setSearchUnitText(val);
                            setCurrentPage(0);
                            }} trackingContext={SearchContext.zones}/>
                            <div className="hidden lg:block">
                                <GridListViewComponent gridView={gridView} setGridView={setGridView} data-testid="managepod-grid-list-view-toggle" />
                            </div>
                        </div>
                        <button
                            className={`bg-theme text-white rounded-md text-sm leading-4 lg:text-base lg:leading-5 font-semibold tracking-[-0.32px] px-5 py-1 flex items-center justify-center h-9 shrink-0 grow-0 hover:bg-theme/80 ${
                                isAccessRestricted ? "opacity-40 cursor-not-allowed" : ""
                            }`}
                            onClick={() => {
                                if (isAccessRestricted) return;
                                setCreateShowPodModal(true);
                            }}
                            disabled={isAccessRestricted}
                            data-testid="managepod-create-zone-button"
                        >
                            Create Zone
                        </button>
                    </div>
                </div>
            </div>

            {/* Grid and List view Zone data */}
            {allPodData?.length > 0 && (
                <>
                    {gridView ? (
                        <ZoneGrid
                            zones={allPodData}
                            getTimeForZone={getTimeForZone}
                            handleSelectZone={handleSelectZone}
                            handleNavigateToUnits={handleNavigateToUnits}
                            toggleDropdown={toggleDropdown}
                            handleEditClick={handleEditClick}
                            handleDeletePod={handleDeletePod}
                            handleGenerateReport={handleGenerateReport}
                            activeDropdownId={activeDropdownId}
                            dropdownRef={dropdownRef}
                            isActionsDisabled={isAccessRestricted}
                        />
                    ) : (
                        <ZoneList
                            zone={allPodData}
                            getTimeForZone={getTimeForZone}
                            activeDropdownId={activeDropdownId}
                            toggleDropdown={toggleDropdown}
                            handleEditClick={handleEditClick}
                            handleDeletePod={handleDeletePod}
                            handleGenerateReport={handleGenerateReport}
                            dropdownRef={dropdownRef}
                            isActionsDisabled={isAccessRestricted}
                        />
                    )}
                    {/* Pagination */}
                    {totalCount > DEFAULT_PAGE_SIZE && (
                        <Pagination pageCount={pageCount} onPageChange={handlePageClick} currentPage={currentPage}/>
                    )}
                </>
            )}

            {/* Loading and when no zones data available */}
            {loading ?
                <>
                    <div className="flex justify-center mt-4 text-gray-500 text-sm" data-testid="managepod-loading-text">
                        Loading More Zones...
                    </div>
                    <LoadingModal />
                </>
                : allPodData?.length === 0 && <div data-testid="managepod-empty-state">
                    <p className="text-center dark:text-white text-dark-gray mt-4">
                        {searchUnitText ? "No zones match your search" : "No zones available"}
                    </p>
                </div>
            }

            {showEditModal && (
                <EditPodModal
                    podData={selectedPodData}
                    setShowPodModal={setShowEditModal}
                    onEditSuccess={() => {
                        showToast("Updated Zone Successfully", "success");
                        setAllPodData([]);
                        fetchPaginatedFleets(false, searchUnitText);

                    }}
                />
            )}

            {showCreatePodModal && (
                <PodModal showPodModal={showCreatePodModal} setShowPodModal={setCreateShowPodModal} onCreateSuccess={() => {
                    showToast("Created Successfully", "success");
                    setAllPodData([])
                    if (pageNumber === 1) {
                        fetchPaginatedFleets(false, searchUnitText); // Reset pagination and fetch again
                    } else {
                        setPageNumber(1)
                    }
                }} />
            )}

            {showConfirmModal && (
                <ConfirmModal
                    title="Confirm Deletion"
                    subText="This action is irreversible"
                    confirmMessage="Are you sure you want to delete this zone?"
                    cancelText="Cancel"
                    submitText="Delete"
                    onCancel={handleCancelDelete}
                    onSubmit={handleConfirmDelete}
                />
            )}

            <ToastMessage
                message={toast.message}
                type={toast.type}
                show={toast.show}
                onClose={() => setToast(prev => ({ ...prev, show: false }))}
            />
            {isGenerateReportModalOpen && (
                <GenerateReportModal
                    isOpen={isGenerateReportModalOpen}
                    onClose={() => {
                        setIsGenerateReportModalOpen(false);
                        setReportPrefillData(null);
                    }}
                    selectedFleet={selectedFleet}
                    allPodData={{ zones: allPodData }}
                    showToast={showToast}
                    prefillData={reportPrefillData}
                />
            )}

        </>
    );
}
